import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchJSON, postJSON, putJSON } from 'api/fetch';

import {
  NO_ROLE,
  SHARED_TIMECLOCK_SLICE,
} from 'features/SharedTimeclock/constants';
import {
  closeTimeClockModal,
  setTimeClockModalError,
  setTimeClockModalView,
} from 'features/SharedTimeclock/TimeClockModal/slice';

import { toI18n } from 'util/i18n';

export type TimeClockType = 'shifts' | 'timecards' | 'timebreaks';

// Helpers
const updateShiftInState = (
  state: SharedTimeclockState,
  updatedShift: SharedTimeclockShift
) => {
  const shiftIndex = state.sharedTimeClockData.shifts.findIndex(
    shift => shift.id === updatedShift.id
  );
  if (shiftIndex !== -1) {
    state.sharedTimeClockData.shifts[shiftIndex] = updatedShift;
  }
};

/**
 * Handles API errors from the daily roster endpoints.
 * Backend returns errors in multiple formats:
 * 1. Plain strings: "Invalid PIN"
 * 2. {error: "..."}: {error: "common.error"}
 * 3. {errors: [...]}: {errors: ["error message"]}
 * 4. Array: [{message: "...", status: 422}]
 */
const handleApiError = async (error: unknown, dispatch: any) => {
  let errorPayload;

  // We clone the response because we have to read it twice
  const apiError = error as any;
  const cloneRes = apiError.response.clone();

  // Try to parse the response body
  try {
    errorPayload = await apiError.response?.json();
  } catch {
    try {
      errorPayload = await cloneRes.text();
    } catch {
      // If both fail, the body was already consumed or unavailable
      errorPayload = null;
    }
  }

  let errorMsg: string;

  // Handle different response formats:
  if (Array.isArray(errorPayload)) {
    errorMsg =
      errorPayload[0]?.message || errorPayload[0] || toI18n('errors.generic');
  } else if (typeof errorPayload === 'string') {
    errorMsg = errorPayload;
  } else if (errorPayload?.errors) {
    errorMsg = Array.isArray(errorPayload.errors)
      ? errorPayload.errors[0]
      : errorPayload.errors;
  } else if (errorPayload?.error) {
    errorMsg = errorPayload.error;
  } else {
    errorMsg = toI18n('errors.generic');
  }

  dispatch(setTimeClockModalError(errorMsg));
  return errorMsg;
};

export interface RateShiftPayload {
  comment?: string;
  rating: number;
  tips?: number;
}

interface BaseShift {
  id: number;
  start_at: string;
  end_at: string | null;
  unscheduled: boolean;
}

export interface SharedTimeclockShift extends BaseShift {
  rating: number | null;
  feedback: string | null;
  user: User;
  role: Role | null;
  timecard?: Timecard | null;
  can_edit?: boolean;
  status?: string;
  color: string;
}

export interface TimeClockShift extends BaseShift {
  color: string;
  owner_id: number;
  owner_type: string;
  role_name: string;
}

export interface Job {
  id: number;
  user_name: string;
  managerial: boolean;
}

export interface MandatedBreak {
  id: number;
  duration: number;
  paid: boolean;
  name: string;
}

export interface Role {
  id: number;
  name: string;
}

interface User {
  id: number;
  name: string;
  avatar_url: string;
}

export interface Timebreak {
  id: number;
  end_at: string | null;
  mandated_break_id: number;
  expected_end_at: string | null;
  duration: number | null;
  is_active: boolean;
  start_at: string;
  mandated_break: MandatedBreak;
}

export interface Timecard {
  id: number;
  cash_tips: number | null;
  start_at: string;
  end_at: string | null;
  duration: string | null;
  timebreaks: Timebreak[];
}

interface Config {
  shift_feedback: boolean;
  shift_tips: boolean;
  prevent_early_clock_in: boolean;
  early_in_min: number;
  tardy_in_min: number;
}

export interface TimeClock {
  roles: Role[];
  type: TimeClockType;
  shifts: TimeClockShift[];
  mandated_breaks: MandatedBreak[];
  job: Job;
  prevent_early_clock_in: boolean;
  current_mandated_break: MandatedBreak | null;
}

export interface SharedTimeclockState {
  error: string | null;
  isLoading: boolean;
  focusedShift: SharedTimeclockShift | undefined;
  pin: string | undefined;
  timeClockData: TimeClock | undefined;
  sharedTimeClockData: {
    date: string;
    shifts: SharedTimeclockShift[];
    mandatedBreaks: MandatedBreak[];
    config: Config;
  };
}

const initialState: SharedTimeclockState = {
  error: null,
  isLoading: true,
  focusedShift: undefined,
  pin: undefined,
  timeClockData: undefined,
  sharedTimeClockData: {
    date: '',
    shifts: [],
    mandatedBreaks: [],
    config: {
      shift_feedback: true,
      shift_tips: false,
      prevent_early_clock_in: false,
      early_in_min: 5,
      tardy_in_min: 5,
    },
  },
};

// Thunks
export const fetchSharedTimeclock = createAsyncThunk(
  `${SHARED_TIMECLOCK_SLICE}/fetchSharedTimeclock`,
  async (_, { rejectWithValue, dispatch }) => {
    try {
      return await fetchJSON('/daily_roster.json');
    } catch (error) {
      return rejectWithValue(await handleApiError(error, dispatch));
    }
  }
);

export const univTimeClock = createAsyncThunk(
  `${SHARED_TIMECLOCK_SLICE}/univTimeClock`,
  async (pin: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await postJSON('/univ_timeclock', { pin });
      dispatch(setTimeClockModalView(response.type));
      return response;
    } catch (error) {
      return rejectWithValue(await handleApiError(error, dispatch));
    }
  }
);

export const clockInShift = createAsyncThunk(
  `${SHARED_TIMECLOCK_SLICE}/clockInShift`,
  async (
    { shiftId, pin }: { shiftId: number; pin: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      return await putJSON(`/shifts/${shiftId}/clock_in`, { pin });
    } catch (error) {
      return rejectWithValue(await handleApiError(error, dispatch));
    }
  }
);

export const clockOutShift = createAsyncThunk(
  `${SHARED_TIMECLOCK_SLICE}/clockOutShift`,
  async (
    {
      shiftId,
      pin,
      showRateShift,
    }: { shiftId: number; pin: string; showRateShift: boolean },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await putJSON(`/shifts/${shiftId}/clock_out`, { pin });
      return response;
    } catch (error) {
      return rejectWithValue(await handleApiError(error, dispatch));
    } finally {
      if (showRateShift) {
        dispatch(setTimeClockModalView('rateShift'));
      }
    }
  }
);

export const createUnscheduledShift = createAsyncThunk(
  `${SHARED_TIMECLOCK_SLICE}/createUnscheduledShift`,
  async (
    { jobId, pin, role }: { jobId: number; pin: string; role: Role },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const payload =
        role?.id !== NO_ROLE.id ? { pin, role_id: role.id } : { pin };
      const response = await postJSON(`/jobs/${jobId}/clock_in`, payload);
      await dispatch(fetchSharedTimeclock());
      return response;
    } catch (error) {
      return rejectWithValue(await handleApiError(error, dispatch));
    }
  }
);

export const clockInBreak = createAsyncThunk(
  `${SHARED_TIMECLOCK_SLICE}/clockInBreak`,
  async (
    { shiftId, pin, mbId }: { shiftId: number; pin: string; mbId: number },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = mbId ? { pin, mb_id: mbId } : { pin };
      return await putJSON(`/shifts/${shiftId}/clock_in_break`, payload);
    } catch (error) {
      return rejectWithValue(await handleApiError(error, dispatch));
    }
  }
);

export const clockOutBreak = createAsyncThunk(
  `${SHARED_TIMECLOCK_SLICE}/clockOutBreak`,
  async (
    { shiftId, pin, mbId }: { shiftId: number; pin: string; mbId: number },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = mbId ? { pin, mb_id: mbId } : { pin };
      return await putJSON(`/shifts/${shiftId}/clock_out_break`, payload);
    } catch (error) {
      return rejectWithValue(await handleApiError(error, dispatch));
    }
  }
);

export const rateShift = createAsyncThunk(
  `${SHARED_TIMECLOCK_SLICE}/rateShift`,
  async (
    { shiftId, payload }: { shiftId: number; payload: RateShiftPayload },
    { rejectWithValue, dispatch }
  ) => {
    try {
      return await putJSON(`/shifts/${shiftId}/rate_shift.json`, payload);
    } catch (error) {
      return rejectWithValue(await handleApiError(error, dispatch));
    }
  }
);

// Slice
const SharedTimeClockSlice = createSlice({
  name: SHARED_TIMECLOCK_SLICE,
  initialState,
  reducers: {
    setTimeClock(state, action) {
      state.timeClockData = action.payload;
    },
    setFocusedShift(state, action) {
      state.focusedShift = action.payload;
    },
    setPin(state, action) {
      state.pin = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSharedTimeclock.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSharedTimeclock.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sharedTimeClockData = {
          date: action.payload.date,
          shifts: action.payload.shifts,
          mandatedBreaks: action.payload.mandated_breaks,
          config: action.payload.config,
        };
      })
      .addCase(fetchSharedTimeclock.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.error.message || 'Failed to fetch shared time clock';
      })
      .addCase(clockInShift.fulfilled, (state, action) => {
        if (action.payload.shift_data) {
          updateShiftInState(state, action.payload.shift_data);
        }
      })
      .addCase(clockOutShift.fulfilled, (state, action) => {
        if (action.payload.shift_data) {
          const updatedShift = action.payload.shift_data;
          const clockOutAt = action.payload.clocked_out_at;

          const shiftIndex = state.sharedTimeClockData.shifts.findIndex(
            shift => shift.id === updatedShift.id
          );

          if (shiftIndex !== -1) {
            state.sharedTimeClockData.shifts[shiftIndex] = updatedShift;
            state.sharedTimeClockData.shifts[shiftIndex].end_at = clockOutAt;
          }
        }
      })
      .addCase(clockInBreak.fulfilled, (state, action) => {
        if (action.payload.shift_data) {
          updateShiftInState(state, action.payload.shift_data);
        }
      })
      .addCase(clockOutBreak.fulfilled, (state, action) => {
        if (action.payload.shift_data) {
          updateShiftInState(state, action.payload.shift_data);
        }
      })
      .addCase(univTimeClock.fulfilled, (state, action) => {
        state.timeClockData = action.payload;

        const isTimecardOrBreak =
          action.payload.type === 'timecards' ||
          action.payload.type === 'timebreaks';
        const firstShiftId = action.payload.shifts?.[0]?.id;

        if (isTimecardOrBreak && firstShiftId) {
          const matchedShift = state.sharedTimeClockData.shifts.find(
            shift => shift.id === firstShiftId
          );
          if (matchedShift) {
            state.focusedShift = matchedShift;
          }
        }
      })
      .addCase(closeTimeClockModal, state => {
        state.focusedShift = undefined;
        state.pin = undefined;
      });
  },
});

export const { setTimeClock, setPin, setFocusedShift, setError } =
  SharedTimeClockSlice.actions;

export const { reducer } = SharedTimeClockSlice;
